/**
 * GIS Configuration - TPL Real Data Integration
 * Central configuration for map, layers, and services
 */

// Tenant detection (runtime, based on hostname)
const _hostname = typeof window !== 'undefined' ? window.location.hostname : '';
export const isWaterTenant = _hostname.includes('water');
export const isEmissionsTenant = _hostname.includes('emissions');

export const mapConfig = {
  center: [
    parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || '31.7'),
    parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || '-102.9')
  ],
  zoom: parseInt(process.env.REACT_APP_MAP_ZOOM || '7'),
  minZoom: 3,
  maxZoom: 18
};

export const arcgisConfig = {
  apiKey: process.env.REACT_APP_ARCGIS_API_KEY,
  basemapStyle: 'arcgis/topographic',
};

/**
 * Feature Service Configuration - Real TPL Data
 */
const tplFeatureServices = {
  // PRIMARY LAYERS (Default Visible)
  tracts: {
    name: 'TPL Land Tracts',
    url: 'http://localhost:5001/api/geojson/tracts',
    geojsonFile: 'tp-assets-all.geojson',
    fields: {
      id: 'OBJECTID',
      tractId: 'TractID',
      tractType: 'TractType',
      county: 'CountyName',
      block: 'Block',
      section: 'Sec',
      acreage: 'TractAcres',
      contractId: 'ContractID',
      status: 'Status',
      orri: 'ORRI',
      nri: 'NRI',
      nma: 'NMA',
      nra: 'NRA',
      dealName: 'DealName',
      grantee: 'Grantee',
      displayName: 'TractID'
    },
    visible: true,
    defaultVisible: true,
    style: {
      fillColor: '#3388ff',
      fillOpacity: 0.3,
      color: '#2c3e50',
      weight: 2
    },
    popupTemplate: {
      title: 'Tract: {TractID}',
      content: [
        { fieldName: 'TractID', label: 'Tract ID' },
        { fieldName: 'TractType', label: 'Tract Type' },
        { fieldName: 'CountyName', label: 'County' },
        { fieldName: 'Block', label: 'Block' },
        { fieldName: 'Sec', label: 'Section' },
        { fieldName: 'TractAcres', label: 'Tract Acres' },
        { fieldName: 'Status', label: 'Status' },
        { fieldName: 'ORRI', label: 'ORRI' },
        { fieldName: 'NRI', label: 'NRI' },
        { fieldName: 'NMA', label: 'Net Mineral Acres' },
        { fieldName: 'DealName', label: 'Deal Name' },
        { fieldName: 'Grantee', label: 'Grantee' }
      ]
    }
  },

  wells: {
    name: 'TPL Royalty Wells',
    url: 'http://localhost:5001/api/geojson/wells',
    geojsonFile: 'tp-royalty-wells.geojson',
    fields: {
      id: 'OBJECTID',
      api: 'API',
      api14: 'API14',
      wellName: 'WellName',
      wellPadId: 'WellPadID',
      activeNri: 'ActiveNRI',
      allocatedNri: 'AllocatedNRI',
      lateralLength: 'LateralLength',
      operator: 'ENVOperator',
      wellStatus: 'ENVWellStatus',
      formation: 'ENVInterval',
      county: 'County',
      dsuName: 'DSUName',
      dealName: 'DealName',
      trajectory: 'Trajectory',
      vintage: 'Vintage',
      displayName: 'WellName'
    },
    visible: true,
    defaultVisible: true,
    geometryType: 'LineString',
    style: {
      color: '#e74c3c',
      weight: 3
    },
    popupTemplate: {
      title: 'Well: {WellName}',
      content: [
        { fieldName: 'WellName', label: 'Well Name' },
        { fieldName: 'API', label: 'API Number' },
        { fieldName: 'ENVOperator', label: 'Operator' },
        { fieldName: 'ENVWellStatus', label: 'Status' },
        { fieldName: 'ENVInterval', label: 'Formation' },
        { fieldName: 'County', label: 'County' },
        { fieldName: 'LateralLength', label: 'Lateral Length (ft)' },
        { fieldName: 'ActiveNRI', label: 'Active NRI' },
        { fieldName: 'DSUName', label: 'DSU Name' },
        { fieldName: 'Trajectory', label: 'Trajectory' },
        { fieldName: 'Vintage', label: 'Vintage' }
      ]
    }
  },

  // LANDGRID LAYERS (Outline Only)
  blocks: {
    name: 'Blocks (Landgrid)',
    url: 'http://localhost:5001/api/geojson/blocks',
    geojsonFile: 'blocks.geojson',
    fields: {
      id: 'OBJECTID',
      blockNumber: 'SURVEYBLO3',
      county: 'CountyName',
      survey: 'SURVEYNAME',
      displayName: 'SURVEYBLO3'
    },
    visible: false,
    defaultVisible: false,
    outlineOnly: true,
    style: {
      fillColor: 'transparent',
      fillOpacity: 0,
      color: '#95a5a6',
      weight: 1,
      dashArray: '5, 5'
    },
    labelField: 'SURVEYBLO3'
  },

  sections: {
    name: 'Sections (Survey)',
    url: 'http://localhost:5001/api/geojson/sections',
    geojsonFile: 'sections.geojson',
    fields: {
      id: 'OBJECTID',
      blkSec: 'BlkSec',
      county: 'County',
      section: 'SURVEYSEC4',
      displayName: 'BlkSec'
    },
    visible: false,
    defaultVisible: false,
    outlineOnly: true,
    style: {
      fillColor: 'transparent',
      fillOpacity: 0,
      color: '#7f8c8d',
      weight: 0.5
    },
    labelField: 'BlkSec'
  },

  // ADDITIONAL LAYERS
  contracts: {
    name: 'TPL Contracts (Easements)',
    url: 'http://localhost:5001/api/geojson/contracts',
    geojsonFile: 'tp-contracts.geojson',
    fields: {
      id: 'OBJECTID',
      tractType: 'TractType',
      tractId: 'TractID',
      county: 'County',
      contractType: 'ContractType',
      contractCat: 'ContractCat',
      status: 'Status',
      grantee: 'Grantee',
      rate: 'Rate',
      effectiveDate: 'EffectiveDate',
      term: 'Term',
      displayName: 'TractID'
    },
    visible: false,
    style: {
      fillColor: '#9b59b6',
      fillOpacity: 0.2,
      color: '#8e44ad',
      weight: 1
    },
    popupTemplate: {
      title: 'Contract: {TractID}',
      content: [
        { fieldName: 'TractID', label: 'Tract ID' },
        { fieldName: 'ContractType', label: 'Contract Type' },
        { fieldName: 'ContractCat', label: 'Category' },
        { fieldName: 'Status', label: 'Status' },
        { fieldName: 'Grantee', label: 'Grantee' },
        { fieldName: 'Rate', label: 'Rate' },
        { fieldName: 'EffectiveDate', label: 'Effective Date' },
        { fieldName: 'Term', label: 'Term' }
      ]
    }
  },

  dsus: {
    name: 'Development Units (DSUs)',
    url: 'http://localhost:5001/api/geojson/dsus',
    geojsonFile: 'dsus.geojson',
    visible: false,
    style: {
      fillColor: '#27ae60',
      fillOpacity: 0.2,
      color: '#1e8449',
      weight: 2
    }
  },

  activeRigs: {
    name: 'Active Rigs',
    url: 'http://localhost:5001/api/geojson/rigs',
    geojsonFile: 'env-active-rigs.geojson',
    visible: false,
    style: {
      color: '#f39c12',
      weight: 4
    }
  }
};

/**
 * Query configuration
 */
export const queryConfig = {
  maxResults: parseInt(process.env.REACT_APP_MAX_QUERY_RESULTS || '1000'),
  outFields: ['*'],
  returnGeometry: true,
  spatialReference: { wkid: 4326 }
};

/**
 * Symbology Configuration - Color schemes for attribute-driven styling
 * This allows dynamic coloring based on feature properties
 */
export const symbologyConfig = {
  // Tract Type Colors (TractType field)
  tractTypes: {
    'F': { color: '#2563eb', fillColor: '#3b82f6', label: 'Fee (Ownership)' },        // Blue - primary ownership
    'O': { color: '#7c3aed', fillColor: '#8b5cf6', label: 'ORRI (Royalty)' },         // Purple - overriding royalty
    'N': { color: '#059669', fillColor: '#10b981', label: 'Non-Participating' },     // Green - non-participating
    'U': { color: '#6b7280', fillColor: '#9ca3af', label: 'Undivided' },              // Gray - undivided
    'Q': { color: '#0891b2', fillColor: '#06b6d4', label: 'Qualified' },              // Cyan - qualified
    'V': { color: '#ea580c', fillColor: '#f97316', label: 'Vendor' },                 // Orange - vendor
    // Compound types (inherit primary)
    'OA': { color: '#7c3aed', fillColor: '#8b5cf6', label: 'ORRI-A' },
    'RF': { color: '#2563eb', fillColor: '#3b82f6', label: 'Royalty Fee' },
    'RC': { color: '#7c3aed', fillColor: '#8b5cf6', label: 'Royalty Contract' },
    'default': { color: '#64748b', fillColor: '#94a3b8', label: 'Other' }            // Slate - default
  },

  // Well Status Colors (ENVWellStatus field)
  wellStatuses: {
    'PRODUCING': { color: '#16a34a', label: 'Producing' },                            // Green - active production
    'DUC': { color: '#eab308', label: 'DUC (Drilled Uncompleted)' },                  // Yellow - drilled but not completed
    'DRILLING': { color: '#dc2626', label: 'Drilling' },                              // Red - active drilling
    'PERMITTED': { color: '#2563eb', label: 'Permitted' },                            // Blue - permitted
    'COMPLETED': { color: '#0891b2', label: 'Completed' },                            // Cyan - completed
    'INACTIVE PRODUCER': { color: '#6b7280', label: 'Inactive Producer' },           // Gray - inactive
    'DRILLED': { color: '#f97316', label: 'Drilled' },                                // Orange - drilled
    'PERMIT EXPIRED': { color: '#9ca3af', label: 'Permit Expired' },                 // Light gray
    'PERMIT CANCELLED': { color: '#d1d5db', label: 'Permit Cancelled' },             // Lighter gray
    'P & A': { color: '#1f2937', label: 'Plugged & Abandoned' },                     // Dark gray - P&A
    'SHUT-IN': { color: '#78716c', label: 'Shut-in' },                                // Stone - shut in
    'default': { color: '#e74c3c', label: 'Other' }                                   // Default red
  },

  // Helper function to get tract style by type
  getTractStyle: function(tractType) {
    const config = this.tractTypes[tractType] || this.tractTypes['default'];
    return {
      color: config.color,
      fillColor: config.fillColor,
      fillOpacity: 0.4,
      weight: 2
    };
  },

  // Helper function to get well style by status
  getWellStyle: function(wellStatus) {
    const config = this.wellStatuses[wellStatus] || this.wellStatuses['default'];
    return {
      color: config.color,
      weight: 3
    };
  }
};

/**
 * Field mappings for natural language processing
 * Maps common terms to actual field names
 */
export const fieldMappings = {
  // Layer identifiers
  layers: {
    'tract': 'tracts',
    'tracts': 'tracts',
    'asset': 'tracts',
    'assets': 'tracts',
    'land': 'tracts',
    'well': 'wells',
    'wells': 'wells',
    'royalty well': 'wells',
    'royalty wells': 'wells',
    'block': 'blocks',
    'blocks': 'blocks',
    'section': 'sections',
    'sections': 'sections',
    'contract': 'contracts',
    'contracts': 'contracts',
    'easement': 'contracts',
    'easements': 'contracts',
    'pipeline': 'contracts',
    'dsu': 'dsus',
    'dsus': 'dsus',
    'development unit': 'dsus',
    'rig': 'activeRigs',
    'rigs': 'activeRigs',
    'active rig': 'activeRigs',
    'active rigs': 'activeRigs'
  },

  // Field name synonyms - TPL specific
  fields: {
    // Tract fields
    'tract id': 'TractID',
    'tractid': 'TractID',
    'tract number': 'TractID',
    'county': 'CountyName',
    'acreage': 'TractAcres',
    'acres': 'TractAcres',
    'tract acres': 'TractAcres',
    'orri': 'ORRI',
    'override': 'ORRI',
    'override royalty': 'ORRI',
    'nri': 'NRI',
    'net royalty interest': 'NRI',
    'nma': 'NMA',
    'net mineral acres': 'NMA',
    'nra': 'NRA',
    'net royalty acres': 'NRA',
    'block': 'Block',
    'section': 'Sec',
    'deal': 'DealName',
    'deal name': 'DealName',
    'grantee': 'Grantee',
    'status': 'Status',

    // Well fields
    'api': 'API',
    'api number': 'API',
    'well name': 'WellName',
    'operator': 'ENVOperator',
    'well status': 'ENVWellStatus',
    'formation': 'ENVInterval',
    'interval': 'ENVInterval',
    'lateral length': 'LateralLength',
    'active nri': 'ActiveNRI',
    'dsu': 'DSUName',
    'dsu name': 'DSUName',
    'trajectory': 'Trajectory',
    'vintage': 'Vintage',

    // Contract fields
    'contract type': 'ContractType',
    'contract category': 'ContractCat',
    'rate': 'Rate',
    'term': 'Term',
    'effective date': 'EffectiveDate',

    // Block/Section fields
    'block number': 'SURVEYBLO3',
    'survey': 'SURVEYNAME',
    'blksec': 'BlkSec'
  },

  // Common operators for fuzzy matching
  operators: [
    'Exxon', 'ExxonMobil',
    'Diamondback', 'Diamondback Energy',
    'Occidental', 'Oxy',
    'Ovintiv',
    'APA Corp', 'Apache',
    'EOG', 'EOG Resources',
    'Mewbourne', 'Mewbourne Oil',
    'Permian Resources',
    'ConocoPhillips', 'COP',
    'Devon', 'Devon Energy'
  ],

  // Status value mappings
  statusValues: {
    'producing': 'PRODUCING',
    'duc': 'DUC',
    'drilled uncompleted': 'DUC',
    'drilling': 'DRILLING',
    'permitted': 'PERMITTED',
    'shut-in': 'SHUT-IN',
    'shut in': 'SHUT-IN',
    'p&a': 'P&A',
    'plugged': 'P&A',
    'active': 'Active',
    'expired': 'Expired',
    'pending': 'Pending'
  },

  // Counties in TPL operating area
  counties: {
    // Texas
    'reeves': 'REEVES',
    'culberson': 'CULBERSON',
    'loving': 'LOVING',
    'martin': 'MARTIN',
    'midland': 'MIDLAND',
    'ector': 'ECTOR',
    'ward': 'WARD',
    'winkler': 'WINKLER',
    'andrews': 'ANDREWS',
    'pecos': 'PECOS',
    'crane': 'CRANE',
    'howard': 'HOWARD',
    'glasscock': 'GLASSCOCK',
    'reagan': 'REAGAN',
    // New Mexico
    'lea': 'LEA',
    'eddy': 'EDDY',
    'chaves': 'CHAVES'
  },

  // Formations
  formations: {
    'wolfcamp': 'WOLFCAMP',
    'wolfcamp a': 'WOLFCAMP A',
    'wolfcamp b': 'WOLFCAMP B',
    'wolfcamp d': 'WOLFCAMP D',
    'bone spring': 'BONE SPRING',
    'avalon': 'AVALON',
    'delaware': 'DELAWARE',
    'spraberry': 'SPRABERRY'
  }
};

/**
 * Water feature services: Select Water Solutions tenant
 */
export const waterFeatureServices = {
  sources: {
    name: 'Water Sources',
    url: '/api/geojson/sources',
    fields: { id: 'id', name: 'name', county: 'county', operator: 'operator', status: 'status', displayName: 'name' },
    visible: true, defaultVisible: true,
    style: { color: '#0284c7', fillColor: '#38bdf8', fillOpacity: 0.6, weight: 2 },
    popupTemplate: {
      title: 'Water Source: {name}',
      content: [
        { fieldName: 'name', label: 'Name' }, { fieldName: 'type', label: 'Type' },
        { fieldName: 'county', label: 'County' }, { fieldName: 'operator', label: 'Operator' },
        { fieldName: 'daily_volume_bbl', label: 'Daily Volume (bbl)' },
        { fieldName: 'quality_tds', label: 'TDS Quality' }, { fieldName: 'status', label: 'Status' }
      ]
    }
  },
  disposal: {
    name: 'Disposal Wells',
    url: '/api/geojson/disposal',
    fields: { id: 'id', name: 'name', county: 'county', operator: 'operator', status: 'status', displayName: 'name' },
    visible: true, defaultVisible: true,
    style: { color: '#7c3aed', fillColor: '#a78bfa', fillOpacity: 0.6, weight: 2 },
    popupTemplate: {
      title: 'Disposal Well: {name}',
      content: [
        { fieldName: 'name', label: 'Name' }, { fieldName: 'county', label: 'County' },
        { fieldName: 'operator', label: 'Operator' }, { fieldName: 'capacity_bbl', label: 'Capacity (bbl)' },
        { fieldName: 'daily_volume_bbl', label: 'Daily Volume (bbl)' },
        { fieldName: 'utilization_pct', label: 'Utilization %' },
        { fieldName: 'formation', label: 'Formation' }, { fieldName: 'status', label: 'Status' }
      ]
    }
  },
  pipelines: {
    name: 'Transfer Pipelines',
    url: '/api/geojson/pipelines',
    fields: { id: 'id', name: 'name', county: 'county', operator: 'operator', status: 'status', displayName: 'name' },
    visible: true, defaultVisible: true,
    geometryType: 'LineString',
    style: { color: '#059669', weight: 3 },
    popupTemplate: {
      title: 'Pipeline: {name}',
      content: [
        { fieldName: 'name', label: 'Name' }, { fieldName: 'county', label: 'County' },
        { fieldName: 'operator', label: 'Operator' }, { fieldName: 'length_miles', label: 'Length (mi)' },
        { fieldName: 'capacity_bbl_day', label: 'Capacity (bbl/day)' },
        { fieldName: 'utilization_pct', label: 'Utilization %' }, { fieldName: 'status', label: 'Status' }
      ]
    }
  },
  treatment: {
    name: 'Treatment Facilities',
    url: '/api/geojson/treatment',
    fields: { id: 'id', name: 'name', county: 'county', operator: 'operator', status: 'status', displayName: 'name' },
    visible: true, defaultVisible: true,
    style: { color: '#ea580c', fillColor: '#fb923c', fillOpacity: 0.6, weight: 2 },
    popupTemplate: {
      title: 'Treatment Facility: {name}',
      content: [
        { fieldName: 'name', label: 'Name' }, { fieldName: 'county', label: 'County' },
        { fieldName: 'operator', label: 'Operator' }, { fieldName: 'capacity_bbl', label: 'Capacity (bbl)' },
        { fieldName: 'recycling_rate_pct', label: 'Recycling Rate %' },
        { fieldName: 'technology', label: 'Technology' }, { fieldName: 'status', label: 'Status' }
      ]
    }
  }
};

/**
 * Emissions feature services: Picarro emissions / gas utility tenant
 * Backend route: GET /api/geojson/:layer  (key matches LAYER_CONFIG in backend routes/geojson.js)
 */
export const emissionsFeatureServices = {
  leaks: {
    name: 'Leak Indications',
    url: '/api/geojson/leaks',
    fields: { id: 'leak_id', severity: 'severity', status: 'status', district: 'district', displayName: 'leak_id' },
    visible: true, defaultVisible: true,
    style: { color: '#dc2626', fillColor: '#ef4444', fillOpacity: 0.8, weight: 2, radius: 6 },
    popupTemplate: {
      title: 'Leak: {leak_id}',
      content: [
        { fieldName: 'leak_id', label: 'Leak ID' },
        { fieldName: 'severity', label: 'Severity' },
        { fieldName: 'status', label: 'Status' },
        { fieldName: 'methane_ppm', label: 'Methane (ppm)' },
        { fieldName: 'district', label: 'District' },
        { fieldName: 'detected_date', label: 'Detected' }
      ]
    }
  },
  transmission_mains: {
    name: 'Transmission Mains',
    url: '/api/geojson/transmission_mains',
    fields: { id: 'pipe_id', material: 'material', diameter: 'diameter_in', district: 'district', displayName: 'pipe_id' },
    visible: false, defaultVisible: false,
    geometryType: 'LineString',
    style: { color: '#1d4ed8', weight: 4 },
    popupTemplate: {
      title: 'Transmission: {pipe_id}',
      content: [
        { fieldName: 'pipe_id', label: 'Pipe ID' },
        { fieldName: 'material', label: 'Material' },
        { fieldName: 'diameter_in', label: 'Diameter (in)' },
        { fieldName: 'install_year', label: 'Install Year' },
        { fieldName: 'district', label: 'District' }
      ]
    }
  },
  distribution_mains: {
    name: 'Distribution Mains',
    url: '/api/geojson/distribution_mains',
    fields: { id: 'pipe_id', material: 'material', diameter: 'diameter_in', district: 'district', displayName: 'pipe_id' },
    visible: true, defaultVisible: true,
    geometryType: 'LineString',
    style: { color: '#0891b2', weight: 2 },
    popupTemplate: {
      title: 'Distribution: {pipe_id}',
      content: [
        { fieldName: 'pipe_id', label: 'Pipe ID' },
        { fieldName: 'material', label: 'Material' },
        { fieldName: 'diameter_in', label: 'Diameter (in)' },
        { fieldName: 'install_year', label: 'Install Year' },
        { fieldName: 'district', label: 'District' }
      ]
    }
  },
  service_lines: {
    name: 'Service Lines',
    url: '/api/geojson/service_lines',
    fields: { id: 'line_id', material: 'material', displayName: 'line_id' },
    visible: false, defaultVisible: false,
    geometryType: 'LineString',
    style: { color: '#64748b', weight: 1 }
  },
  survey_routes: {
    name: 'Survey Routes',
    url: '/api/geojson/survey_routes',
    fields: { id: 'route_id', crew: 'crew', status: 'status', displayName: 'route_id' },
    visible: false, defaultVisible: false,
    geometryType: 'LineString',
    style: { color: '#7c3aed', weight: 3, dashArray: '4, 4' }
  },
  pipe_risk: {
    name: 'Pipe Risk Scores',
    url: '/api/geojson/pipe_risk',
    fields: { id: 'pipe_id', risk_score: 'risk_score', displayName: 'pipe_id' },
    visible: false, defaultVisible: false,
    geometryType: 'LineString',
    style: { color: '#f59e0b', weight: 3 }
  },
  sensitive_premises: {
    name: 'Sensitive Premises',
    url: '/api/geojson/sensitive_premises',
    fields: { id: 'premise_id', type: 'type', displayName: 'premise_id' },
    visible: false, defaultVisible: false,
    style: { color: '#be185d', fillColor: '#ec4899', fillOpacity: 0.5, weight: 2, radius: 5 }
  },
  work_orders_open: {
    name: 'Open Work Orders',
    url: '/api/geojson/work_orders_open',
    fields: { id: 'wo_id', priority: 'priority', status: 'status', displayName: 'wo_id' },
    visible: false, defaultVisible: false,
    style: { color: '#ea580c', fillColor: '#fb923c', fillOpacity: 0.7, weight: 2, radius: 5 }
  },
  work_orders_closed: {
    name: 'Completed Repairs',
    url: '/api/geojson/work_orders_closed',
    fields: { id: 'wo_id', displayName: 'wo_id' },
    visible: false, defaultVisible: false,
    style: { color: '#16a34a', fillColor: '#22c55e', fillOpacity: 0.5, weight: 2, radius: 4 }
  },
  districts: {
    name: 'Districts',
    url: '/api/geojson/districts',
    fields: { id: 'district_id', name: 'name', displayName: 'name' },
    visible: false, defaultVisible: false,
    style: { color: '#475569', fillColor: '#cbd5e1', fillOpacity: 0.1, weight: 1, dashArray: '3, 3' }
  },
  crews: {
    name: 'Field Crews',
    url: '/api/geojson/crews',
    fields: { id: 'crew_id', name: 'name', status: 'status', displayName: 'name' },
    visible: false, defaultVisible: false,
    style: { color: '#0d9488', fillColor: '#14b8a6', fillOpacity: 0.8, weight: 2, radius: 7 }
  },
  facility: {
    name: 'Facility Boundary',
    url: '/api/geojson/facility',
    fields: { id: 'facility_id', name: 'name', displayName: 'name' },
    visible: false, defaultVisible: false,
    style: { color: '#0f172a', fillColor: '#94a3b8', fillOpacity: 0.15, weight: 2 }
  },
  process_units: {
    name: 'Process Units',
    url: '/api/geojson/process_units',
    fields: { id: 'unit_id', type: 'type', displayName: 'unit_id' },
    visible: false, defaultVisible: false,
    style: { color: '#7c2d12', fillColor: '#a16207', fillOpacity: 0.5, weight: 2 }
  },
  sensors: {
    name: 'Fenceline Sensors',
    url: '/api/geojson/sensors',
    fields: { id: 'sensor_id', type: 'type', status: 'status', displayName: 'sensor_id' },
    visible: false, defaultVisible: false,
    style: { color: '#15803d', fillColor: '#22c55e', fillOpacity: 0.9, weight: 2, radius: 5 }
  },
  dispersion_plumes: {
    name: 'Dispersion Plumes',
    url: '/api/geojson/dispersion_plumes',
    fields: { id: 'plume_id', concentration: 'concentration', displayName: 'plume_id' },
    visible: false, defaultVisible: false,
    style: { color: '#b91c1c', fillColor: '#fca5a5', fillOpacity: 0.35, weight: 1 }
  }
};

// Dynamic named export: all importers get the right set for this tenant
export const featureServices = isEmissionsTenant
  ? emissionsFeatureServices
  : isWaterTenant
    ? waterFeatureServices
    : tplFeatureServices;

export default {
  mapConfig,
  arcgisConfig,
  featureServices,
  queryConfig,
  fieldMappings
};
